import { type FormEvent, useEffect, useMemo, useState } from 'react';
import {
  createTrainingRequest,
  getTrainingRequest,
  listTrainingRequests,
  removeTrainingRequestPhoto,
  returnTrainingRequest,
  startTrainingRequestReview,
  submitTrainingRequest,
  trainingRequestPhotoUrl,
  updateTrainingRequest,
  uploadTrainingRequestPhoto,
  type CatalogItem,
  type Project,
  type TrainingRequest,
  type TrainingRequestInput,
  type User,
} from './api';
import { MaterialDraftPanel } from './MaterialDraftPanel';

const currentMonth = new Date().toISOString().slice(0, 7);
const initialTopicNames = [
  '新規入場・基本ルール',
  '車両系建設機械接触',
  'ダンプ後退',
  '掘削・土砂崩壊',
  'クレーン・吊荷',
  '玉掛け',
  '墜落・転落',
  '挟まれ・巻き込まれ',
  '熱中症',
  '第三者災害',
  '交通事故',
  '冬季施工',
  '凍結・転倒',
  '除雪作業',
  '吹雪・視界不良',
  '電動工具',
  '災害・緊急時対応',
  'リスクアセスメント',
] as const;

const specificWorkCandidates = [
  '掘削',
  '残土積込',
  '残土運搬',
  '路床整形',
  '路盤',
  '舗装',
  '排水構造物',
  '法面',
  '資材搬入',
  '重機回送',
  '除雪',
] as const;

const dumpReverseConditions = [
  { code: 'LOADING_AREA', label: '積込場所' },
  { code: 'UNLOADING_AREA', label: '荷下ろし場所' },
  { code: 'TURNING_SPACE', label: '切返しスペース' },
  { code: 'PUBLIC_TRAFFIC_CROSSING', label: '一般車両との交錯' },
  { code: 'WORKER_CROSSING', label: '作業員との交錯' },
  { code: 'SPOTTER_PRESENT', label: '誘導員あり' },
  { code: 'EXCAVATOR_PROXIMITY', label: 'バックホウ等との近接' },
  { code: 'NIGHT_OR_TWILIGHT', label: '夜間・薄暮' },
  { code: 'SLOPE', label: '傾斜' },
  { code: 'NARROW_AREA', label: '狭小部' },
  { code: 'ROAD_SHOULDER', label: '路肩' },
  { code: 'PUBLIC_ROAD_ENTRANCE', label: '公道出入口' },
] as const;

const isDumpReverseTopic = (value: string): boolean => {
  const normalized = value.replace(/\s+/gu, '');
  return normalized.includes('ダンプ') && normalized.includes('後退');
};

const specificWorkCandidateSet = new Set<string>(specificWorkCandidates);

function uniqueLines(value: string): string[] {
  return [
    ...new Set(
      value
        .split(/[\n,、]/u)
        .map((item) => item.trim())
        .filter(Boolean),
    ),
  ];
}

function emptyInput(project: Project): TrainingRequestInput {
  return {
    customEquipment: [],
    customWorkItems: [],
    equipmentTypeIds: project.equipmentTypeIds ?? [],
    nearMiss: null,
    occurrenceNo: 1,
    remarks: null,
    requestedTopicText: '',
    scheduledOn: null,
    specialAttention: null,
    targetMonth:
      currentMonth < project.plannedStartOn.slice(0, 7)
        ? project.plannedStartOn.slice(0, 7)
        : currentMonth > project.plannedCompleteOn.slice(0, 7)
          ? project.plannedCompleteOn.slice(0, 7)
          : currentMonth,
    themeConditionCodes: [],
    topicMode: 'REQUESTED',
    workTypeIds: project.workTypeIds ?? [],
  };
}

function requestInput(request: TrainingRequest): TrainingRequestInput {
  return {
    customEquipment: request.customEquipment ?? [],
    customWorkItems: request.customWorkItems ?? [],
    equipmentTypeIds: request.equipmentTypeIds ?? [],
    nearMiss: request.nearMiss,
    occurrenceNo: request.occurrenceNo,
    remarks: request.remarks,
    requestedTopicText: request.requestedTopicText ?? '',
    scheduledOn: request.scheduledOn,
    specialAttention: request.specialAttention,
    targetMonth: request.targetMonth,
    themeConditionCodes: request.themeConditionCodes ?? [],
    topicMode: 'REQUESTED',
    workTypeIds: request.workTypeIds ?? [],
  };
}

function statusLabel(request: TrainingRequest): string {
  if (request.status === 'DRAFT') return '下書き';
  if (request.status === 'REVIEWING') return 'One Color確認中';
  if (request.returnedReason !== null) return '差戻し';
  return '依頼済み（One Color確認待ち）';
}

export function TrainingRequestsPanel({
  companyId,
  csrfToken,
  equipmentTypes,
  onError,
  project,
  user,
  workTypes,
}: {
  companyId: string;
  csrfToken: string;
  equipmentTypes: CatalogItem[];
  onError: (error: unknown) => void;
  project: Project;
  user: User;
  workTypes: CatalogItem[];
}) {
  const [requests, setRequests] = useState<TrainingRequest[]>([]);
  const [selected, setSelected] = useState<TrainingRequest | null>(null);
  const [draft, setDraft] = useState<TrainingRequestInput>(() => emptyInput(project));
  const [files, setFiles] = useState<File[]>([]);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [completionDialogOpen, setCompletionDialogOpen] = useState(false);
  const [returnReason, setReturnReason] = useState('');
  const [standardMaterialOverrideReason, setStandardMaterialOverrideReason] = useState('');
  const [createKey, setCreateKey] = useState(() => crypto.randomUUID());
  const [submitKey, setSubmitKey] = useState(() => crypto.randomUUID());
  const editable =
    selected === null ||
    selected.status === 'DRAFT' ||
    (selected.status === 'REQUESTED' && selected.returnedReason !== null);
  const superAdmin = user.globalRole === 'SUPER_ADMIN';

  const photoCount = selected?.photos?.length ?? selected?.photoCount ?? 0;
  const remainingPhotoSlots = Math.max(10 - photoCount, 0);
  const inputMinutes = useMemo(() => {
    const filled = [
      draft.specialAttention,
      draft.nearMiss,
      draft.requestedTopicText,
      draft.scheduledOn,
      draft.remarks,
    ].filter(Boolean).length;
    return filled >= 4 ? '入力はほぼ完了です' : '目安3分で入力できます';
  }, [draft]);
  const draftQualityWarnings = useMemo(() => {
    const warnings: string[] = [];
    if (draft.customWorkItems.length === 0) {
      warnings.push(
        '今月の具体作業が不足しています。有料品質の教材を作成するため、最低1つ以上選択してください。',
      );
    }
    if (isDumpReverseTopic(draft.requestedTopicText) && draft.themeConditionCodes.length === 0) {
      warnings.push('ダンプ後退の現場条件を最低1つ以上選択してください。');
    }
    return warnings;
  }, [draft.customWorkItems, draft.requestedTopicText, draft.themeConditionCodes]);

  async function refresh(preferredId?: string): Promise<void> {
    const next = await listTrainingRequests(companyId, project.id);
    setRequests(next);
    const id = preferredId ?? selected?.id;
    if (id !== undefined) {
      const detail = await getTrainingRequest(companyId, project.id, id);
      setSelected(detail);
      setDraft(requestInput(detail));
      setStandardMaterialOverrideReason('');
    }
  }

  useEffect(() => {
    setSelected(null);
    setDraft(emptyInput(project));
    setFiles([]);
    setCompletionDialogOpen(false);
    setStandardMaterialOverrideReason('');
    setCreateKey(crypto.randomUUID());
    setSubmitKey(crypto.randomUUID());
    void listTrainingRequests(companyId, project.id).then(setRequests).catch(onError);
  }, [companyId, project.id]);

  function toggle(field: 'equipmentTypeIds' | 'workTypeIds', id: string): void {
    const current = draft[field];
    setDraft({
      ...draft,
      [field]: current.includes(id) ? current.filter((item) => item !== id) : [...current, id],
    });
  }

  function toggleSpecificWork(value: string): void {
    setDraft({
      ...draft,
      customWorkItems: draft.customWorkItems.includes(value)
        ? draft.customWorkItems.filter((item) => item !== value)
        : [...draft.customWorkItems, value],
    });
  }

  function toggleThemeCondition(code: string): void {
    setDraft({
      ...draft,
      themeConditionCodes: draft.themeConditionCodes.includes(code)
        ? draft.themeConditionCodes.filter((item) => item !== code)
        : [...draft.themeConditionCodes, code],
    });
  }

  async function choose(requestId: string): Promise<void> {
    setBusy(true);
    setNotice(null);
    try {
      const detail = await getTrainingRequest(companyId, project.id, requestId);
      setSelected(detail);
      setDraft(requestInput(detail));
      setFiles([]);
      setSubmitKey(crypto.randomUUID());
    } catch (error) {
      onError(error);
    } finally {
      setBusy(false);
    }
  }

  function beginNew(): void {
    setSelected(null);
    setDraft(emptyInput(project));
    setFiles([]);
    setNotice(null);
    setCompletionDialogOpen(false);
    setCreateKey(crypto.randomUUID());
    setSubmitKey(crypto.randomUUID());
  }

  async function persist(): Promise<TrainingRequest> {
    let saved =
      selected === null
        ? await createTrainingRequest(companyId, project.id, draft, csrfToken, createKey)
        : await updateTrainingRequest(
            companyId,
            project.id,
            selected.id,
            { ...draft, version: selected.version },
            csrfToken,
          );
    if (selected === null) setCreateKey(crypto.randomUUID());
    const pending = files.slice(0, Math.max(10 - saved.photoCount, 0));
    for (let index = 0; index < pending.length; index += 1) {
      await uploadTrainingRequestPhoto(companyId, project.id, saved.id, pending[index]!, csrfToken);
      setFiles(pending.slice(index + 1));
    }
    saved = await getTrainingRequest(companyId, project.id, saved.id);
    setSelected(saved);
    setDraft(requestInput(saved));
    await refresh(saved.id);
    return saved;
  }

  async function save(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setBusy(true);
    setNotice(null);
    try {
      await persist();
      setNotice('下書きを保存しました。');
    } catch (error) {
      onError(error);
    } finally {
      setBusy(false);
    }
  }

  async function submit(): Promise<void> {
    setBusy(true);
    setNotice(null);
    try {
      const saved = await persist();
      const submitted = await submitTrainingRequest(
        companyId,
        project.id,
        saved.id,
        saved.version,
        csrfToken,
        submitKey,
      );
      setSubmitKey(crypto.randomUUID());
      await refresh(submitted.id);
      setNotice('依頼を受け付けました。現在はOne Color確認待ちです。');
      setCompletionDialogOpen(true);
    } catch (error) {
      onError(error);
    } finally {
      setBusy(false);
    }
  }

  async function review(): Promise<void> {
    if (selected === null) return;
    setBusy(true);
    try {
      const updated = await startTrainingRequestReview(
        companyId,
        project.id,
        selected.id,
        selected.version,
        csrfToken,
        selected.inputQualityWarnings !== undefined && selected.inputQualityWarnings.length > 0
          ? standardMaterialOverrideReason
          : null,
      );
      await refresh(updated.id);
      setStandardMaterialOverrideReason('');
      setNotice('One Color確認を開始しました。');
    } catch (error) {
      onError(error);
    } finally {
      setBusy(false);
    }
  }

  async function returnForCorrection(): Promise<void> {
    if (selected === null) return;
    setBusy(true);
    try {
      const updated = await returnTrainingRequest(
        companyId,
        project.id,
        selected.id,
        selected.version,
        returnReason,
        csrfToken,
      );
      setReturnReason('');
      await refresh(updated.id);
      setNotice('企業へ差し戻しました。');
    } catch (error) {
      onError(error);
    } finally {
      setBusy(false);
    }
  }

  async function removePhoto(photoId: string): Promise<void> {
    if (selected === null) return;
    setBusy(true);
    try {
      await removeTrainingRequestPhoto(companyId, project.id, selected.id, photoId, csrfToken);
      await refresh(selected.id);
      setNotice('写真を削除しました。');
    } catch (error) {
      onError(error);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="request-workspace">
        <aside className="card request-list">
          <div className="section-heading">
            <div>
              <p className="eyebrow">MONTHLY REQUESTS</p>
              <h2>{project.name}</h2>
            </div>
            <button className="secondary" onClick={beginNew} type="button">
              新規入力
            </button>
          </div>
          {requests.length === 0 ? (
            <p className="empty-state">教材依頼はまだありません。</p>
          ) : (
            requests.map((request) => (
              <button
                className={`request-row ${selected?.id === request.id ? 'selected' : ''}`}
                key={request.id}
                onClick={() => void choose(request.id)}
                type="button"
              >
                <span>
                  <strong>{request.targetMonth.replace('-', '年')}月</strong>
                  <small>
                    第{request.occurrenceNo}回 · 写真{request.photoCount}枚
                  </small>
                </span>
                <span className={`status-chip ${request.status.toLowerCase()}`}>
                  {statusLabel(request)}
                </span>
              </button>
            ))
          )}
        </aside>

        <form className="card monthly-form" onSubmit={(event) => void save(event)}>
          <div className="section-heading">
            <div>
              <p className="eyebrow">3–5 MINUTE INPUT</p>
              <h2>{selected === null ? '今月の教材依頼' : `${selected.targetMonth} の教材依頼`}</h2>
              <p className="muted">{inputMinutes}</p>
            </div>
            {selected !== null && <span className="status-chip">{statusLabel(selected)}</span>}
          </div>

          {notice !== null && <div className="notice success">{notice}</div>}
          {selected?.returnedReason !== null && selected?.returnedReason !== undefined && (
            <div className="notice warning">
              <strong>One Colorからの差戻し</strong>
              <span>{selected.returnedReason}</span>
            </div>
          )}
          {editable && draftQualityWarnings.length > 0 && (
            <div className="notice warning" role="alert">
              <strong>入力品質の確認</strong>
              {draftQualityWarnings.map((warning) => (
                <span key={warning}>{warning}</span>
              ))}
            </div>
          )}

          <div className="monthly-grid">
            <label>
              対象年月
              <input
                disabled={!editable}
                max={project.plannedCompleteOn.slice(0, 7)}
                min={project.plannedStartOn.slice(0, 7)}
                onChange={(event) => setDraft({ ...draft, targetMonth: event.target.value })}
                required
                type="month"
                value={draft.targetMonth}
              />
            </label>
            <label>
              同月内の回数
              <input
                disabled={!editable}
                max="12"
                min="1"
                onChange={(event) =>
                  setDraft({ ...draft, occurrenceNo: Number(event.target.value) })
                }
                type="number"
                value={draft.occurrenceNo}
              />
            </label>
            <label>
              実施予定日
              <input
                disabled={!editable}
                onChange={(event) =>
                  setDraft({ ...draft, scheduledOn: event.target.value || null })
                }
                type="date"
                value={draft.scheduledOn ?? ''}
              />
            </label>
            <label>
              希望テーマ
              <select
                disabled={!editable}
                onChange={(event) => {
                  const requestedTopicText = event.target.value;
                  setDraft({
                    ...draft,
                    requestedTopicText,
                    themeConditionCodes: isDumpReverseTopic(requestedTopicText)
                      ? draft.themeConditionCodes
                      : [],
                  });
                }}
                required
                value={draft.requestedTopicText}
              >
                <option value="">希望テーマを選択</option>
                {initialTopicNames.map((topic) => (
                  <option key={topic} value={topic}>
                    {topic}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <fieldset disabled={!editable}>
            <legend>工事区分（複数可）</legend>
            <div className="check-grid compact-checks">
              {workTypes
                .filter((item) => item.status === 'ACTIVE')
                .map((item) => (
                  <label className="check-label" key={item.id}>
                    <input
                      checked={draft.workTypeIds.includes(item.id)}
                      onChange={() => toggle('workTypeIds', item.id)}
                      type="checkbox"
                    />
                    {item.name}
                  </label>
                ))}
            </div>
            <small>工事登録の区分です。今月の具体作業は次の欄で選択してください。</small>
          </fieldset>

          <fieldset disabled={!editable}>
            <legend>今月の具体作業（必須・複数可）</legend>
            <div className="check-grid compact-checks">
              {specificWorkCandidates.map((work) => (
                <label className="check-label" key={work}>
                  <input
                    checked={draft.customWorkItems.includes(work)}
                    onChange={() => toggleSpecificWork(work)}
                    type="checkbox"
                  />
                  {work}
                </label>
              ))}
            </div>
            <label>
              その他の具体作業（改行または読点区切り）
              <textarea
                onChange={(event) => {
                  const selectedCandidates = draft.customWorkItems.filter((item) =>
                    specificWorkCandidateSet.has(item),
                  );
                  setDraft({
                    ...draft,
                    customWorkItems: [
                      ...selectedCandidates,
                      ...uniqueLines(event.target.value).filter(
                        (item) => !specificWorkCandidateSet.has(item),
                      ),
                    ],
                  });
                }}
                placeholder="例：路肩掘削、残土を仮置場へ運搬"
                value={draft.customWorkItems
                  .filter((item) => !specificWorkCandidateSet.has(item))
                  .join('\n')}
              />
            </label>
          </fieldset>

          {isDumpReverseTopic(draft.requestedTopicText) && (
            <fieldset disabled={!editable}>
              <legend>ダンプ後退の現場条件（必須・複数可）</legend>
              <small>今回の現場に当てはまる条件を選択してください。</small>
              <div className="check-grid condition-checks">
                {dumpReverseConditions.map((condition) => (
                  <label className="check-label" key={condition.code}>
                    <input
                      checked={draft.themeConditionCodes.includes(condition.code)}
                      onChange={() => toggleThemeCondition(condition.code)}
                      type="checkbox"
                    />
                    {condition.label}
                  </label>
                ))}
              </div>
            </fieldset>
          )}

          <fieldset disabled={!editable}>
            <legend>今月使用する機械（複数可）</legend>
            <div className="check-grid compact-checks">
              {equipmentTypes
                .filter((item) => item.status === 'ACTIVE')
                .map((item) => (
                  <label className="check-label" key={item.id}>
                    <input
                      checked={draft.equipmentTypeIds.includes(item.id)}
                      onChange={() => toggle('equipmentTypeIds', item.id)}
                      type="checkbox"
                    />
                    {item.name}
                  </label>
                ))}
            </div>
            <label>
              その他の機械（改行または読点区切り）
              <textarea
                onChange={(event) =>
                  setDraft({ ...draft, customEquipment: uniqueLines(event.target.value) })
                }
                value={draft.customEquipment.join('\n')}
              />
            </label>
          </fieldset>

          <label>
            特に注意したいこと
            <textarea
              disabled={!editable}
              maxLength={4000}
              onChange={(event) =>
                setDraft({ ...draft, specialAttention: event.target.value || null })
              }
              placeholder="例：バックホウ旋回時の接触防止、誘導員との合図確認"
              value={draft.specialAttention ?? ''}
            />
          </label>
          <label>
            ヒヤリハット（任意）
            <textarea
              disabled={!editable}
              maxLength={4000}
              onChange={(event) => setDraft({ ...draft, nearMiss: event.target.value || null })}
              value={draft.nearMiss ?? ''}
            />
          </label>
          <label>
            備考
            <textarea
              disabled={!editable}
              maxLength={2000}
              onChange={(event) => setDraft({ ...draft, remarks: event.target.value || null })}
              value={draft.remarks ?? ''}
            />
          </label>

          <fieldset disabled={!editable}>
            <legend>現場写真（最大10枚）</legend>
            <small>
              初月など現場がまだ動いていない場合は、写真なしでも依頼できます。現場写真がある場合は添付してください。
            </small>
            {selected?.photos !== undefined && selected.photos.length > 0 && (
              <div className="photo-grid">
                {selected.photos.map((photo) => (
                  <figure key={photo.id}>
                    <img
                      alt={`現場写真 ${photo.sortOrder + 1}`}
                      src={trainingRequestPhotoUrl(companyId, project.id, selected.id, photo.id)}
                    />
                    <figcaption>
                      {photo.width}×{photo.height}
                      <button
                        className="danger ghost"
                        disabled={busy}
                        onClick={() => void removePhoto(photo.id)}
                        type="button"
                      >
                        削除
                      </button>
                    </figcaption>
                  </figure>
                ))}
              </div>
            )}
            <label>
              JPEG・PNG・WebP／1枚10MB・20MP以下（残り{remainingPhotoSlots}枚）
              <input
                accept="image/jpeg,image/png,image/webp"
                disabled={!editable || remainingPhotoSlots === 0}
                multiple
                onChange={(event) => {
                  const selectedFiles = Array.from(event.target.files ?? []);
                  setFiles(selectedFiles.slice(0, remainingPhotoSlots));
                }}
                type="file"
              />
            </label>
            {files.length > 0 && <small>{files.length}枚を次回保存時にアップロードします。</small>}
          </fieldset>

          {editable && (
            <div className="request-actions">
              <button className="secondary" disabled={busy} type="submit">
                {busy ? '処理中…' : '下書きを保存'}
              </button>
              <button
                disabled={
                  busy ||
                  draft.requestedTopicText.trim().length === 0 ||
                  draftQualityWarnings.length > 0
                }
                onClick={() => void submit()}
                type="button"
              >
                教材作成を依頼
              </button>
            </div>
          )}

          {superAdmin && selected?.status === 'REQUESTED' && selected.returnedReason === null && (
            <div className="review-panel">
              <h3>One Color確認</h3>
              {selected.inputQualityWarnings !== undefined &&
                selected.inputQualityWarnings.length > 0 && (
                  <>
                    <div className="notice warning">
                      <strong>入力品質の警告</strong>
                      {selected.inputQualityWarnings.map((warning) => (
                        <span key={warning}>{warning}</span>
                      ))}
                    </div>
                    <label>
                      標準教材として進める理由（10文字以上）
                      <textarea
                        maxLength={1000}
                        onChange={(event) => setStandardMaterialOverrideReason(event.target.value)}
                        value={standardMaterialOverrideReason}
                      />
                    </label>
                    <small>
                      企業入力が不足しているため、現場条件を推測せず標準模式図と標準確認項目を使います。
                    </small>
                  </>
                )}
              <button
                disabled={
                  busy ||
                  (selected.inputQualityWarnings !== undefined &&
                    selected.inputQualityWarnings.length > 0 &&
                    standardMaterialOverrideReason.trim().length < 10)
                }
                onClick={() => void review()}
                type="button"
              >
                {selected.inputQualityWarnings !== undefined &&
                selected.inputQualityWarnings.length > 0
                  ? '標準教材として確認を開始'
                  : '確認を開始'}
              </button>
            </div>
          )}
          {superAdmin && selected?.status === 'REVIEWING' && (
            <>
              <MaterialDraftPanel
                companyId={companyId}
                csrfToken={csrfToken}
                onError={onError}
                projectId={project.id}
                requestId={selected.id}
              />
              <div className="review-panel">
                <h3>差戻し</h3>
                <label>
                  企業への修正依頼（10文字以上）
                  <textarea
                    onChange={(event) => setReturnReason(event.target.value)}
                    value={returnReason}
                  />
                </label>
                <button
                  className="danger"
                  disabled={busy || returnReason.trim().length < 10}
                  onClick={() => void returnForCorrection()}
                  type="button"
                >
                  修正を依頼
                </button>
              </div>
            </>
          )}
        </form>
      </div>
      {completionDialogOpen && (
        <div
          className="modal-backdrop"
          onClick={(event) => {
            if (event.target === event.currentTarget) setCompletionDialogOpen(false);
          }}
          role="presentation"
        >
          <section
            aria-labelledby="request-complete-title"
            aria-modal="true"
            className="completion-dialog"
            onKeyDown={(event) => {
              if (event.key === 'Escape') setCompletionDialogOpen(false);
            }}
            role="dialog"
          >
            <p className="eyebrow">REQUEST COMPLETED</p>
            <h2 id="request-complete-title">依頼が完了しました</h2>
            <p>出来上がりまで1週間程度お待ちください。</p>
            <button autoFocus onClick={() => setCompletionDialogOpen(false)} type="button">
              確認
            </button>
          </section>
        </div>
      )}
    </>
  );
}
